import type {
  CollapsableMenu,
  SubMenuNavigation,
  SubMenu,
  LinkData,
  MainMenuNavigation,
  Menu,
  MenuWithSubMenus,
} from "~/stores/navigation.ts";

/**
 * Computed Navigation Hierarchy
 */
type NavHierarchyRoot = CollapsableMenu | (CollapsableMenu & Required<SubMenuNavigation[string]>);
export type NavHierarchyNode = NavHierarchyRoot | SubMenu | LinkData;
export type NavHierarchy = NavHierarchyRoot[];
type NavHierarchyNodePruneFn = <T extends NavHierarchyNode>(node: T) => T | undefined;

export function getNavigationHierarchy(
  main: MainMenuNavigation,
  subs: Record<string, Menu | MenuWithSubMenus | null>,
): NavHierarchy {
  return main.map((item) => ({
    ...item,
    ...(subs[item.key] ?? {}),
  }));
}

function pruneNodeLinks<T extends NavHierarchyNode>(node: T, pruneFn: NavHierarchyNodePruneFn): T | undefined {
  if (!("links" in node)) return pruneFn(node);

  const links = compactMap(node.links, pruneFn);
  return links.length ? ({ ...node, links } as T) : undefined;
}

function pruneNodeSubMenus<T extends NavHierarchyNode>(node: T, pruneFn: NavHierarchyNodePruneFn): T | undefined {
  if (!("subMenus" in node)) return node;

  const subMenus = compactMap(node.subMenus, (subMenu) => safeApply(pruneNodeLinks(subMenu, pruneFn), pruneFn));
  return subMenus.length ? ({ ...node, subMenus } as T) : undefined;
}
/**
 * Iterative 3 level function to to filter menu items based on a filter function
 *  - a node with submenus/links is filtered out only if all children are filtered out
 *  - all nodes will be filtered out if does not pass the filter function
 */
export function filterNavHierarchy(
  navHierarchy: NavHierarchy,
  filterFn: (node: NavHierarchyNode) => boolean,
): NavHierarchy {
  const pruneFn: NavHierarchyNodePruneFn = (n) => (filterFn(n) ? n : undefined);
  return $ck._.compact(
    navHierarchy.map((node) => safeApply(pruneNodeLinks(node, pruneFn), (n) => pruneNodeSubMenus(n, pruneFn))),
  );
}

/**
 * Flattens the navigation hierarchy into a single array of nodes
 *  - this is useful for searching or filtering the entire hierarchy
 *  - this will not preserve the original structure of the hierarchy
 */
export function flattenHierarchy(navHierarchy: NavHierarchy): NavHierarchyNode[] {
  const flattenLinks = (node: NavHierarchyNode): NavHierarchyNode[] =>
    "links" in node ? [node, ...node.links] : [node];

  return navHierarchy
    .map((node) => ("subMenus" in node ? [node, ...node.subMenus.map(flattenLinks).flat()] : flattenLinks(node)))
    .flat();
}
